import {
  useBuyCreditsDialog,
} from "./index";
import {
  usePaymentVerificationDialog,
} from "~/services/payment-verification-dialog/index";
import {
  usePersonalCreditsStore,
} from "~/stores/personal-credits";
import {
  useSessionStore,
} from "~/stores/session";
import {
  ElMessage,
} from "element-plus";

interface BuyCreditsFlowOptions {
  showPaymentVerification?: boolean;
}

export async function buyCreditsFlow(
  onSuccess: () => Promise<void>,
  options: BuyCreditsFlowOptions = {},
) {
  const {
    showPaymentVerification = true,
  } = options;
  const sessionStore = useSessionStore();
  const personalCreditsStore = usePersonalCreditsStore();
  const apiFetch = useApiFetch();
  const {$t} = useNuxtApp();
  const paymentStatus = await sessionStore.fetchRegistrationPaymentStatus(true);
  if (paymentStatus?.isSchoolPaused) {
    ElMessage.warning($t("MY_CREDITS_USAGE.SCHOOL_PAUSED_BUY_BLOCKED"));
    return;
  }
  const pendingInvoiceNumber = paymentStatus?.pendingOrder?.invoiceNumber;
  if (pendingInvoiceNumber) {
    const existingOrder = await apiFetch<any>(
      `/api/payments/orders/${encodeURIComponent(pendingInvoiceNumber)}`,
    );
    await usePaymentVerificationDialog(
      existingOrder.order,
      existingOrder.payment,
      async () => {
        await Promise.all([
          onSuccess(),
          sessionStore.fetchRegistrationPaymentStatus(true),
          personalCreditsStore.refresh(),
        ]);
      },
    );
    return;
  }

  const [confirmed, orderRes] = await useBuyCreditsDialog(async (pkg) => {
    try {
      const response = await apiFetch<any>("/api/payments/orders", {
        method: "POST",
        body: {
          planKey: pkg.key,
          couponCode: pkg.couponCode,
          checkout: pkg.checkout,
        },
      });
      await sessionStore.fetchRegistrationPaymentStatus(true);
      return response;
    } catch (error: any) {
      const statusCode = error?.statusCode ?? error?.status ?? error?.response?.status;
      if (statusCode !== 409) {
        throw error;
      }
      const currentStatus = await sessionStore.fetchRegistrationPaymentStatus(true);
      const currentInvoiceNumber = currentStatus?.pendingOrder?.invoiceNumber;
      if (!currentInvoiceNumber) {
        throw error;
      }
      return apiFetch<any>(`/api/payments/orders/${encodeURIComponent(currentInvoiceNumber)}`);
    }
  });

  if (!confirmed || !orderRes || !showPaymentVerification) {
    return;
  }

  await usePaymentVerificationDialog(
    orderRes.order,
    orderRes.payment,
    async () => {
      await Promise.all([
        onSuccess(),
        sessionStore.fetchRegistrationPaymentStatus(true),
        personalCreditsStore.refresh(),
      ]);
    },
  );
}
